import handlePayloadMutators from './handlePayloadMutators';
import isObject from './isObject';
import replaceSlugs from './replaceSlugs';

/**
 *
 * @param {string|Object} input - String or object that should have slugs replaced.
 * @param {Object} eventData - The data from the current event
 * @param {Object} configuration - A tracking project configuration.
 * @param {Object} utils The current project/grouping utils.
 * @param {Object} mapping This is the whole mapping object for the current mapping configuration name
 * @param {Object} flags A feature flags object
 * 	ex. window.DDC.WidgetData['ws-tagging'].mapping['critial_mass']
 * @returns {string|Object} String or object with slugs replaced. It can handle slugs in the property names (keys) or values of an object.
 */
const formatSlugs = ({
	input,
	eventData,
	configuration = {},
	utils,
	mapping,
	flags,
	payload
}) => {
	const { lookups, payloadMutator } = configuration;
	let formattedInput;

	if (isObject(input)) {
		formattedInput = {};
		Object.entries(input).forEach(([payloadKey, payloadValue]) => {
			const formattedSlug = formatSlugs({
				input: payloadValue,
				eventData,
				configuration,
				utils,
				mapping,
				flags,
				payload
			});

			// this gets the renamedKey
			// if renamedKey is undefined that means the slug was not found
			// if renamedKey is defined then it may be a resolved slug
			const renamedKey = replaceSlugs({
				input: payloadKey,
				eventData,
				mapping,
				utils,
				lookups,
				flags,
				payload
			});

			let key = payloadKey;

			// if the key was renamed (it was a slug) or no slug was found
			// set the key that will be set to formattedPayload to the renamedKey
			if (payloadKey !== renamedKey) {
				key = renamedKey;
			}

			if (key) {
				formattedInput[key] = handlePayloadMutators(
					key,
					formattedSlug,
					payloadMutator
				);
			}
		});
	} else if (typeof input === 'string') {
		formattedInput = replaceSlugs({
			input,
			eventData,
			mapping,
			utils,
			lookups,
			flags,
			payload
		});
		// Check if there is a hierarchy of returnables in the string
		if (input.includes(' >> ')) {
			const hierarchicalValues = input.split(' >> ');
			const tempValues = hierarchicalValues.map((el) =>
				formatSlugs({
					input: el,
					eventData,
					configuration,
					utils,
					mapping,
					flags,
					payload
				})
			);
			formattedInput = tempValues.find((el) => {
				return el !== null && el !== undefined && el !== '';
			});
		}
		// this line allows shared payloads to support slugs
		if (isObject(formattedInput)) {
			formattedInput = formatSlugs({
				input: formattedInput,
				eventData,
				configuration,
				utils,
				mapping,
				flags,
				payload
			});
		}
	} else if (Array.isArray(input)) {
		formattedInput = input.map((el) =>
			formatSlugs({
				input: el,
				eventData,
				configuration,
				utils,
				mapping,
				flags,
				payload
			})
		);
	} else {
		// extra safety in case something unexpected comes through
		// most data is parseable, however there are exceptionally rare cases where input gets passed as a function or undefined
		// function and undefined will throw an error when we attempt to use JSON.parse
		formattedInput =
			typeof input !== 'function' && typeof input !== 'undefined'
				? JSON.parse(JSON.stringify(input))
				: undefined;
	}

	return formattedInput;
};

export default formatSlugs;
